import React from "react";
import {
  Box,
  Button,
  Chip,
  FormControlLabel,
  Grid,
  Paper,
  Stack,
  Typography,
  Alert,
  Checkbox,
} from "@mui/material";
import PersonAddAltOutlinedIcon from "@mui/icons-material/PersonAddAltOutlined";
import ChevronRightIcon from "@mui/icons-material/ChevronRight";
import type { ChildDetailTab, ParentChild } from "../../../types/parentFamily";
import { formatShortDate } from "../../../utils/parentFamilyHelpers";
import { formatLedgerCurrency, summarizeSchoolFees } from "../../../utils/schoolFeesHelpers";
import { FAMILY_INFO_CARD_SX, FAMILY_TEXT_LINK_SX } from "./familyStyles";

interface UserCircleOption {
  circleId: string;
  name: string;
  alias?: string;
}

interface ChildOverviewSectionProps {
  child: ParentChild;
  userId: string;
  userNameById: Map<string, string>;
  userCircles: UserCircleOption[];
  isSaving: boolean;
  onEditChild: () => void;
  onLinkGuardian: () => void;
  onRemoveGuardian: (guardianUserId: string) => void;
  onSetChildDetailTab: (tab: ChildDetailTab) => void;
  onOpenFeeSetup: (childId: string) => void;
  onUpdateChildCircles: (childId: string, circleIds: string[]) => void;
  renderLinkedCircles: (child: ParentChild) => React.ReactNode;
}

export const ChildOverviewSection: React.FC<ChildOverviewSectionProps> = ({
  child,
  userId,
  userNameById,
  onEditChild,
  onLinkGuardian,
  onRemoveGuardian,
  onSetChildDetailTab,
  onOpenFeeSetup,
  isSaving,
  renderLinkedCircles,
}) => {
  const fees = summarizeSchoolFees(child.schoolFees);
  const account = child.schoolFees;
  const otherChargeCount = child.feeAccounts?.length ?? 0;

  const statusLabel = child.status === "inactive" ? "Inactive" : "Active";

  const familyInfoRows: [string, string][] = [
    ["Date of birth", formatShortDate(child.dateOfBirth)],
    ["Gender", child.gender || "—"],
    ["Grade", child.grade || "—"],
    ["Class", child.classSection?.trim() || "—"],
    ["Student ID", child.studentId?.trim() || "—"],
    ["Status", statusLabel],
  ];

  return (
    <Grid container spacing={1.75}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Paper variant="outlined" sx={FAMILY_INFO_CARD_SX}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.25 }}
          >
            <Typography variant="subtitle2">Family information</Typography>
            <Button sx={FAMILY_TEXT_LINK_SX} onClick={onEditChild}>
              Edit
            </Button>
          </Stack>
          <Stack spacing={0.85}>
            {familyInfoRows.map(([label, value]) => (
              <Stack
                key={label}
                direction="row"
                justifyContent="space-between"
                gap={2}
              >
                <Typography variant="body2" color="text.secondary">
                  {label}
                </Typography>
                <Typography variant="body2" textAlign="right">
                  {value}
                </Typography>
              </Stack>
            ))}
          </Stack>
          {child.notes ? (
            <Box
              sx={{
                mt: 1.5,
                pt: 1.5,
                borderTop: "1px solid",
                borderColor: "divider",
              }}
            >
              <Typography variant="caption" color="text.secondary">
                Notes
              </Typography>
              <Typography variant="body2">{child.notes}</Typography>
            </Box>
          ) : null}
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper variant="outlined" sx={FAMILY_INFO_CARD_SX}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.25 }}
          >
            <Typography variant="subtitle2">Linked parents</Typography>
            <Button
              sx={FAMILY_TEXT_LINK_SX}
              startIcon={<PersonAddAltOutlinedIcon sx={{ fontSize: 16 }} />}
              onClick={onLinkGuardian}
            >
              Link parent
            </Button>
          </Stack>
          <Stack spacing={1}>
            {(child.guardians || []).length === 0 ? (
              <Typography variant="body2" color="text.secondary">
                No linked parents yet.
              </Typography>
            ) : (
              (child.guardians || []).map((g) => (
                <Stack
                  key={g.userId}
                  direction="row"
                  justifyContent="space-between"
                  alignItems="center"
                  gap={1}
                >
                  <Box sx={{ minWidth: 0 }}>
                    <Typography variant="body2" noWrap>
                      {userNameById.get(g.userId) || g.userId}
                    </Typography>
                    <Typography variant="caption" color="text.secondary">
                      {g.role}
                      {g.userId === userId ? " · this account" : ""}
                    </Typography>
                  </Box>
                  {g.userId !== userId ? (
                    <Button
                      size="small"
                      color="error"
                      sx={{ ...FAMILY_TEXT_LINK_SX, color: "error.main" }}
                      onClick={() => void onRemoveGuardian(g.userId)}
                      disabled={isSaving}
                    >
                      Unlink
                    </Button>
                  ) : null}
                </Stack>
              ))
            )}
          </Stack>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper variant="outlined" sx={FAMILY_INFO_CARD_SX}>
          <Stack
            direction="row"
            justifyContent="space-between"
            alignItems="center"
            sx={{ mb: 1.25 }}
          >
            <Typography variant="subtitle2">Fees at a glance</Typography>
            {account && fees.isSettled ? (
              <Chip size="small" label="Paid up" color="success" />
            ) : null}
          </Stack>
          {account ? (
            <Stack spacing={1}>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Outstanding
                </Typography>
                <Typography
                  variant="body2"
                  color={fees.isSettled ? "success.main" : "error.main"}
                >
                  {fees.isSettled
                    ? "Paid up"
                    : formatLedgerCurrency(fees.outstanding, account.currency)}
                </Typography>
              </Stack>
              <Stack direction="row" justifyContent="space-between">
                <Typography variant="body2" color="text.secondary">
                  Paid
                </Typography>
                <Typography variant="body2" color="success.main">
                  {formatLedgerCurrency(fees.totalPaid, account.currency)}
                </Typography>
              </Stack>
              <Typography variant="body2" color="text.secondary">
                {account.academicYear} ·{" "}
                {formatLedgerCurrency(fees.totalAnnualFee, account.currency)} total
                {fees.paymentCount > 0
                  ? ` · ${fees.paymentCount} of ${account.paymentPlanMonths} payments`
                  : ""}
                {otherChargeCount > 0
                  ? ` · ${otherChargeCount} other charge${otherChargeCount !== 1 ? "s" : ""}`
                  : ""}
              </Typography>
            </Stack>
          ) : (
            <Typography variant="body2" color="text.secondary">
              No school fees set up yet. Add the annual statement or other charges on
              the fee statements tab.
            </Typography>
          )}
          <Stack spacing={0.75} sx={{ mt: 1.25 }}>
            <Button
              sx={FAMILY_TEXT_LINK_SX}
              endIcon={<ChevronRightIcon sx={{ fontSize: 18 }} />}
              onClick={() => onSetChildDetailTab("fee-statements")}
            >
              Open fee statements
            </Button>
            {!account ? (
              <Button
                sx={FAMILY_TEXT_LINK_SX}
                onClick={() => onOpenFeeSetup(child.id)}
              >
                Set up school fees
              </Button>
            ) : null}
          </Stack>
        </Paper>
      </Grid>

      <Grid size={{ xs: 12, md: 6 }}>
        <Paper variant="outlined" sx={FAMILY_INFO_CARD_SX}>
          <Typography variant="subtitle2" gutterBottom>
            Linked circles
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 1 }}>
            Choose which of this parent&apos;s circles apply to {child.firstName}.
          </Typography>
          {renderLinkedCircles(child)}
        </Paper>
      </Grid>
    </Grid>
  );
};

export function renderLinkedCirclesPanel(
  child: ParentChild,
  userCircles: UserCircleOption[],
  onUpdateChildCircles: (childId: string, circleIds: string[]) => void
) {
  if (userCircles.length === 0) {
    return (
      <Alert severity="info" sx={{ mt: 1 }}>
        This parent is not in any subcircles yet. Add them to circles first, then link this
        family member.
      </Alert>
    );
  }

  return (
    <Stack spacing={0.5} sx={{ mt: 1 }}>
      {userCircles.map((circle) => {
        const checked = child.linkedCircleIds.includes(circle.circleId);
        return (
          <FormControlLabel
            key={circle.circleId}
            control={
              <Checkbox
                checked={checked}
                onChange={(e) => {
                  const next = e.target.checked
                    ? [...child.linkedCircleIds, circle.circleId]
                    : child.linkedCircleIds.filter((id) => id !== circle.circleId);
                  onUpdateChildCircles(child.id, next);
                }}
                size="small"
              />
            }
            label={
              <Box>
                <Typography variant="body2">{circle.name}</Typography>
                {circle.alias ? (
                  <Typography variant="caption" color="text.secondary">
                    Parent alias: {circle.alias}
                  </Typography>
                ) : null}
              </Box>
            }
          />
        );
      })}
    </Stack>
  );
}
